import React from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";

import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";

import Card from "@mui/material/Card";
import {Checkbox, FormControlLabel, Grid} from "@mui/material";
import MDLoadingButton from "../../../components/MDLoadingButton";
import {useUserSettingsMutation} from "../query";


const SettingsCard = ({user}) => {
    const {t} = useTranslation(['user']);
    const {userSettings: userSettings = {}} = user;

    const visibility = userSettings?.model1ColumnsVisibility ?? {};

    const userSettingsMutation = useUserSettingsMutation(user.id);

    const formik = useFormik({
        initialValues: {
            ww: visibility.ww ?? true,
            n: visibility.n ?? true,
            v: visibility.v ?? true,
            vq1: visibility.vq1 ?? true,
            vq2: visibility.vq2 ?? true,
            ta: visibility.ta ?? true,
            tMin: visibility.tMin ?? true,
            tMax: visibility.tMax ?? true,
            hs: visibility.hs ?? true,
            hn: visibility.hn ?? true,
            hnAux: visibility.hnAux ?? true,
            ps: visibility.ps ?? true,
            th10: visibility.th10 ?? true,
            th30: visibility.th30 ?? true,
            pr: visibility.pr ?? true,
            pf: visibility.pf ?? true,
            sf: visibility.sf ?? true,
            sh: visibility.sh ?? true,
            l1: visibility.l1 ?? true,
            l2: visibility.l2 ?? true,
            l3: visibility.l3 ?? true,
            l4: visibility.l4 ?? true,
            l5: visibility.l5 ?? true,
            l6: visibility.l6 ?? true,
            l7: visibility.l7 ?? true,
            l8: visibility.l8 ?? true,
            l9: visibility.l9 ?? true,
            notes: visibility.notes ?? true
        },
        onSubmit: async (values) => {
            try {
                await userSettingsMutation.mutateAsync({model1ColumnsVisibility: values});
                formik.resetForm({values});  // reset form
            } catch (e) {
                // Do nothing because the error is already handled by the FeedbackProvider
            }
        }
    });

    const disableSave = !formik.dirty;

    return <Card>
        <MDBox py={2} px={2}>
            <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                {t('settingsCard.title')}
            </MDTypography>
            <MDTypography variant="h6">
                {t('settingsCard.model1ColumnsVisibility')}
            </MDTypography>

            <Grid container columnSpacing={4} rowSpacing={4}>
                <Grid item xs={12} md={2}>
                    <CheckBoxGroup formik={formik}
                                   groupName="Gruppo 9"
                                   params={["ww", "n", "v", "vq1", "vq2", "ta", "tMin", "tMax"]}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <CheckBoxGroup formik={formik}
                                   groupName="Gruppo 8"
                                   params={["hs", "hn", "hnAux", "ps", "th10", "th30", "pr", "pf", "sf", "sh"]}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <CheckBoxGroup formik={formik}
                                   groupName="Gruppo 7"
                                   params={["l1", "l2", "l3", "l4", "l5", "l6"]}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <CheckBoxGroup formik={formik}
                                   groupName="Gruppo 6"
                                   params={["l7", "l8", "l9"]}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <CheckBoxGroup formik={formik}
                                   groupName="Gruppo 5"
                                   params={["notes"]}/>
                </Grid>
            </Grid>
        </MDBox>

        <MDBox sx={{p: 4, textAlign: "center"}}>
            <MDLoadingButton variant="contained"
                             color="dark"
                             loading={userSettingsMutation.isLoading}
                             onClick={formik.handleSubmit}
                             disabled={disableSave}>
                {t('cardBasicInfo.submit')}
            </MDLoadingButton>
        </MDBox>
    </Card>
}

const CheckBoxGroup = ({formik, groupName, params}) => {
    const groupValues = Object.entries(formik.values).filter(([k, _]) => params.includes(k)).map(([_, v]) => v);

    const isGroupChecked = groupValues.every(v => v)
    const isGroupIndeterminate = !isGroupChecked && groupValues.some(v => v)

    const handleGroupSelect = (event) => {
        const check = event.target.checked;
        formik.setValues({...formik.values, ...params.reduce((acc, p) => {
                acc[p] = check;
                return acc;
            }, {})})
    }

    return <>
        <FormControlLabel label={groupName} control={
            <Checkbox checked={isGroupChecked}
                      indeterminate={isGroupIndeterminate}
                      color="info"
                      onChange={handleGroupSelect}
            />
        }
        />
        <MDBox sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
        {
            params.map(paramName => (
                <FormControlLabel
                    key={paramName}
                    label={paramName}
                    control={<Checkbox checked={formik.values[paramName]}
                                       onChange={formik.handleChange}
                                       name={paramName}
                                       color="info"
                                       inputProps={{'aria-label': 'controlled'}}/>}
                />
            ))
        }
        </MDBox>
    </>
}

export default SettingsCard;